/**
 * Module dependencies.
 */

import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import {
  CrudAddType,
  CrudEditType,
  CrudListType,
  CrudRemoveType,
  CrudTemplate,
  FormList,
  ListColumns
} from 'app/components/organisms/crud/form-types';
import { normalizeTemplate } from 'app/components/organisms/crud/utils';
import { apiEndpoints } from 'app/core/config/api-endpoints';
import { requiredRule } from 'app/core/utils/field-rules';
import { appRoutes } from 'app/routes';
import { CopyId } from 'app/components/atoms/copy-id/copy-id';
import { TFunction } from 'i18next';
import React from 'react';

/**
 * List columns.
 */

const listColumns = (translate: TFunction): ListColumns => [
  {
    title: 'ID',
    dataIndex: 'id',
    size: '110px',
    key: 'id',
    render: (item: any) => <CopyId value={item.id} />
  },
  {
    title: translate('poolInvest.categories.name'),
    dataIndex: 'name',
    size: '1fr',
    key: 'name'
  },
  {
    title: translate('poolInvest.categories.kind'),
    dataIndex: 'kind',
    size: '160px',
    key: 'kind',
    render: (item: any) => translate(`poolInvest.bucketKind.${item.kind}`)
  },
  {
    title: translate('poolInvest.categories.asset'),
    dataIndex: 'assetSymbol',
    size: '140px',
    key: 'assetSymbol',
    render: (item: any) => item.assetSymbol || '—'
  }
];

/**
 * Form fields (shared by add + edit). The asset symbol only applies to crypto
 * categories — left blank for cash.
 */

const formFields = (translate: TFunction): FormList => [
  {
    type: 'inputField',
    name: 'name',
    label: translate('poolInvest.categories.name'),
    rules: requiredRule(translate)
  },
  {
    type: 'selectField',
    name: 'kind',
    label: translate('poolInvest.categories.kind'),
    rules: requiredRule(translate),
    options: [
      { value: 'cash', label: translate('poolInvest.bucketKind.cash') },
      { value: 'crypto', label: translate('poolInvest.bucketKind.crypto') }
    ]
  },
  {
    type: 'inputField',
    name: 'assetSymbol',
    label: translate('poolInvest.categories.assetHint'),
    rules: undefined
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: String(translate('common.labels.search'))
  }
];

/**
 * Add.
 */

const add = (translate: TFunction): CrudAddType => ({
  formFields: formFields(translate)
});

/**
 * Edit.
 */

const edit = (translate: TFunction): CrudEditType => ({
  formFields: formFields(translate)
});

/**
 * Remove.
 */

const remove = (): CrudRemoveType => ({});

/**
 * List.
 */

const list = (translate: TFunction): CrudListType => ({
  columns: listColumns(translate),
  style: { minWidth: 500 },
  route: appRoutes.dashboard.poolInvest.projectCategories,
  key: ['poolInvestBuckets'],
  endpoint: apiEndpoints.poolInvestProjectBuckets,
  filters: listFilters(translate)
});

/**
 * Config.
 */

export function createPoolInvestCategoriesTemplate(translate: TFunction): CrudTemplate {
  return normalizeTemplate(translate, {
    add: add(translate),
    edit: edit(translate),
    list: list(translate),
    remove: remove()
  });
}
